import React from "react";
import { Linkedin, Twitter, Instagram, Facebook } from "lucide-react";
import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Monogram } from "./Monogram";

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Twitter, label: "X" },
  { icon: Instagram, label: "Instagram" },
  { icon: Facebook, label: "Facebook" },
];

const LINKS = [
  { label: "About", to: "/#about" },
  { label: "Residential", to: "/residential" },
  { label: "Commercial", to: "/commercial" },
  { label: "Builders", to: "/builders" },
  { label: "Developers", to: "/developers" },
  { label: "Investors", to: "/investors" },
];

export const Footer = () => {
  const lenis = useLenis();
  const navigate = useNavigate();
  return (
    <footer className="relative overflow-hidden bg-ink border-t border-charcoal/50 pt-28 pb-12" data-testid="footer">
      <img
        src={`${process.env.PUBLIC_URL || ""}/htc-emblem.png`}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-10 w-72 md:w-96 opacity-[0.05] select-none"
      />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 pb-24">
          <div className="lg:col-span-5">
            <Monogram size={60} />
            <p className="mt-8 font-serif italic text-3xl md:text-4xl text-taupe/70 font-light leading-tight max-w-sm">
              Where luxury real estate closes.
            </p>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <div className="overline mb-6 text-taupe">Navigate</div>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => lenis?.scrollTo(0, { duration: 1.4 })}
                  data-testid="footer-link-home"
                  className="text-ivory hover:text-gold transition-colors duration-300 text-sm font-light"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => lenis?.scrollTo("#locations", { offset: -20, duration: 1.4 })}
                  data-testid="footer-link-locations"
                  className="text-ivory hover:text-gold transition-colors duration-300 text-sm font-light"
                >
                  Locations
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/podcast")}
                  data-testid="footer-link-podcast"
                  className="text-ivory hover:text-gold transition-colors duration-300 text-sm font-light"
                >
                  Podcast
                </button>
              </li>
              {LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => navigate(l.to)}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-taupe/70 hover:text-taupe transition-colors duration-300 text-sm font-light"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-6 text-taupe">Contact</div>
            <ul className="space-y-3 text-sm font-light">
              <li>
                <a href="tel:+12816459502" data-testid="footer-phone" className="text-ivory hover:text-gold transition-colors duration-300">
                  281-645-9502
                </a>
              </li>
              <li>
                <a href="mailto:info@houstontitletx.com" data-testid="footer-email" className="text-ivory hover:text-gold transition-colors duration-300">
                  info@houstontitletx.com
                </a>
              </li>
              <li>
                <a href="https://houstontitletx.com" target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="text-taupe/80 hover:text-gold transition-colors duration-300">
                  www.houstontitletx.com
                </a>
              </li>
              <li className="text-taupe/70">Houston, Texas</li>
            </ul>
            <div className="flex items-center gap-5 mt-8">
              {SOCIALS.map(({ icon: Icon, label }) => (
                <button
                  key={label}
                  onClick={() => toast("Coming Soon", { description: `Follow us on ${label} at launch.` })}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="text-taupe hover:text-gold transition-colors duration-300"
                >
                  <Icon size={18} strokeWidth={1.2} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hairline mb-8" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-taupe/50 text-xs font-light tracking-wide">
            © {new Date().getFullYear()} Houston Title Co. All rights reserved.
          </p>
          <p className="overline text-[0.55rem] text-taupe/50">Experience · Integrity · Results</p>
        </div>
      </div>
    </footer>
  );
};
